'use client'

import React, { useState, useRef } from 'react';
import htmlParser from 'html-react-parser';
import Modal from '../Modal/Modal';
import './Accordion.css';
import useRateDuvidaFrequente from "../../hooks/requestQueries/duvidasFrequentes/rateDuvidaFrequente";

interface AccordionItem {
    id: number;
    pergunta: string;
    resposta: string;
}

interface AccordionProps {
    items: AccordionItem[];
    showFeedback?: boolean;
    itemClassName?: string;
}

const Accordion: React.FC<AccordionProps> = ({ items, showFeedback = true, itemClassName = '' }) => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [showThanksModal, setShowThanksModal] = useState<boolean>(false);
    const [isFeedbackPositive, setIsFeedbackPositive] = useState<boolean | null>(null);

    const modalRef = useRef<HTMLDivElement | null>(null);

    const toggleAccordion = (index: number) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const handleKeyDown = (event: React.KeyboardEvent, index: number) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleAccordion(index);
        }
    };

    const { mutate: rateDuvida } = useRateDuvidaFrequente();

    const handleFeedback = (positive: boolean, id: number) => {
        rateDuvida({ id, util: positive });
        setIsFeedbackPositive(positive);
        setShowThanksModal(true);

        setTimeout(() => {
            modalRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
    };

    return (
        <>
            <section className="accordion">
                {items.map((item, index) => {
                    const isActive = activeIndex === index;
                    return (
                        <div key={index} className={`accordion-item ${itemClassName}`}>
                            <h5 className="accordion-title">
                                <div
                                    id={`accordion-header-${index}`}
                                    className="accordion-header"
                                    onClick={() => toggleAccordion(index)}
                                    onKeyDown={(event) => handleKeyDown(event, index)}
                                    role="button"
                                    aria-expanded={isActive}
                                    aria-controls={`content-${index}`}
                                    aria-label={`Abrir/Fechar seção: ${item.pergunta}`}
                                    tabIndex={0}
                                >
                                    <span>{item.pergunta}</span>
                                    <svg className="accordion-icon" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                                        <path fill="currentColor" d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"></path>
                                    </svg>
                                </div>
                            </h5>
                            <div
                                id={`content-${index}`}
                                className={`accordion-content-wrapper ${isActive ? 'accordion-content-wrapper--active' : ''}`}
                                role="region"
                                aria-labelledby={`accordion-header-${index}`}
                                aria-hidden={!isActive}
                                tabIndex={0}
                            >
                                <div className="accordion-content">
                                    {htmlParser(item.resposta)}

                                    {isActive && showFeedback && (
                                        <div className="feedback-section">
                                            <p tabIndex={0}>Esse conteúdo foi útil para você?</p>
                                            <div className="containerBtns">
                                                <button
                                                    className="btnFeedbackBlue"
                                                    onClick={() => handleFeedback(true, item.id)}
                                                >
                                                    Sim
                                                </button>
                                                <button
                                                    className="btnFeedbackBlue200"
                                                    onClick={() => handleFeedback(false, item.id)}
                                                >
                                                    Não
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </section>

            {showThanksModal && (
                <Modal
                    message={isFeedbackPositive ? "Agradecemos sua avaliação." : "Agradecemos seu feedback."}
                    onClose={() => setShowThanksModal(false)}
                    modalRef={modalRef}
                />
            )}
        </>
    );
};

export default Accordion;